// Recent Changes (PD-16593) filters — whose match decisions, how far back,
// which decisions, and a search. They render the filter tray on the Recent
// Changes list (listKey 'license_match_changes'). The backend handler is the
// custom GET /api/licenses/matches/changes endpoint, which reads these keys
// from the filters JSON (changeWho / changeWindow / changeAction /
// changeKeywords), so no shared queryMapping is needed here. Option ids are
// non-numeric so they survive the list's URL state (see
// RECENT_MATCH_CHANGE_WINDOWS).

import {
	RECENT_MATCH_CHANGE_ACTIONS,
	RECENT_MATCH_CHANGE_DEFAULT_WINDOW,
	RECENT_MATCH_CHANGE_WHO,
	RECENT_MATCH_CHANGE_WINDOWS,
} from '@supply-mover-2-0/shared';
import { createKeywordFilter } from './factories';
import type { FilterRegistry } from './types';

const defaultWindow = RECENT_MATCH_CHANGE_WINDOWS.find(
	(window) => window.id === RECENT_MATCH_CHANGE_DEFAULT_WINDOW,
);

export const licenseMatchChangeFilters: FilterRegistry = {
	changeKeywords: {
		...createKeywordFilter({
			label: 'Search',
			placeholder: 'Search account or license...',
			objectTable: 'client_license_match_log',
			listKeys: ['license_match_changes'],
		}),
		queryParamName: 'changeKeywords',
	},

	// Whose decisions: the caller's own by default (the list's filterRules),
	// with teammates and Workd Automation as toggles.
	changeWho: {
		type: 'multi-select',
		label: 'Made By',
		placeholder: 'Select who...',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'changeWho',
		multiple: true,
		valueExtractor: {
			property: 'id',
		},
		options: RECENT_MATCH_CHANGE_WHO.map((who) => ({
			id: who.id,
			label: who.label,
		})),
		format: 'array',
		listKeys: ['license_match_changes'],
	},

	changeWindow: {
		type: 'radio',
		label: 'When',
		colorScheme: 'green',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'changeWindow',
		multiple: false,
		options: RECENT_MATCH_CHANGE_WINDOWS.map((window) => ({
			id: window.id,
			label: window.label,
		})),
		defaultValue:
			defaultWindow != null
				? { id: defaultWindow.id, label: defaultWindow.label }
				: undefined,
		format: 'single',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['license_match_changes'],
	},

	changeAction: {
		type: 'multi-select',
		label: 'Change',
		placeholder: 'Select change...',
		colorScheme: 'purple',
		searchable: false,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'changeAction',
		multiple: true,
		valueExtractor: {
			property: 'id',
		},
		options: RECENT_MATCH_CHANGE_ACTIONS.map((action) => ({
			id: action.id,
			label: action.label,
		})),
		format: 'array',
		listKeys: ['license_match_changes'],
	},
};
